import { useState, useEffect, useMemo, useCallback } from 'react';

export type PageSize = 10 | 25 | 50 | 100 | 'all';

export function usePagination<T>(items: T[], initialPageSize: PageSize = 25) {
	const queries = new URLSearchParams(location.search);
	
	const getPageFromUrl = (): number => {
		const page = parseInt(queries.get('page') || '1', 10);
		return isNaN(page) || page <= 0 ? 1 : page;
	};

	const getPageSizeFromUrl = (): PageSize => {
		const pageSizeParam = queries.get('pageSize') || '25';
		if(pageSizeParam === 'all'){
			return 'all';
		}
		const parsed = parseInt(pageSizeParam, 10);
		if([10, 25, 50, 100].includes(parsed)){
			return parsed as PageSize;
		}
		return 25;
	};

	const [currentPage, setCurrentPage] = useState(getPageFromUrl());
	const [currentPageSize, setCurrentPageSize] = useState<PageSize>(getPageSizeFromUrl());

	const maxPage = useMemo(() => {
		if(currentPageSize === 'all'){
			return 1;
		}
		return Math.ceil(items.length / currentPageSize) || 1;
	}, [items.length, currentPageSize]);

	const paginatedItems = useMemo(() => {
		if(currentPageSize === 'all'){
			return items;
		}
		const start = (currentPage - 1) * currentPageSize;
		const end = start + currentPageSize;
		return items.slice(start, end);
	}, [items, currentPage, currentPageSize]);

	useEffect(() => {
		if(currentPage > maxPage && maxPage > 0){
			setCurrentPage(maxPage);
			const url = new URL(location.href);
			if(maxPage === 1){
				url.searchParams.delete('page');
			} else {
				url.searchParams.set('page', maxPage.toString());
			}
			history.replaceState({}, '', url.toString());
		}
	}, [currentPage, maxPage]);

	const changePage = (pageNumber: number) => {
		const url = new URL(location.href);
		if(pageNumber === 1){
			url.searchParams.delete('page');
		} else {
			url.searchParams.set('page', pageNumber.toString());
		}
		history.replaceState({}, '', url.toString());
		setCurrentPage(pageNumber);
	}

	const changePageSize = (newPageSize: PageSize) => {
		const url = new URL(location.href);
		if(newPageSize === 25){
			url.searchParams.delete('pageSize');
		} else {
			url.searchParams.set('pageSize', newPageSize.toString());
		}
		url.searchParams.delete('page');
		history.replaceState({}, '', url.toString());
		setCurrentPageSize(newPageSize);
		setCurrentPage(1);
	}

	const resetPage = useCallback(() => {
		setCurrentPage(1);
		const url = new URL(location.href);
		url.searchParams.delete('page');
		history.replaceState({}, '', url.toString());
	}, []);

	return {
		currentPage,
		currentPageSize,
		maxPage,
		paginatedItems,
		changePage,
		changePageSize,
		resetPage,
	};
}

